import tmsServiceBackend from '@/services/axios/tmsServiceBackend';
import { isNil, isNumber } from 'lodash-es';
import Moment from 'moment/moment';
import qs from 'qs';

enum ShipmentStatus {
    PickupCreated = 1,
    PickupDispatched = 2,
    PickedCompleted = 3,
    InTransit = 4,
    Out4Delivery = 5,
    Delivered = 6,
}

interface IShipmentDto {
    bol_created: number;
    consignee_id: string;
    customer: string;
    delivery_agent_order_id: string;
    delivery_date: string;
    delivery_location_city: string;
    delivery_location_state: string;
    delivery_name: string;
    is_trip_order_created_from_lh: number;
    m_lines: IMlineDto[];
    order_adjusted: IAdjustedOrderLineDto[];
    order_final: number;
    order_id: number;
    order_note: string;
    order_po: string;
    order_pro: null | number;
    order_reason: string;
    order_ref: string;
    order_stage: number;
    order_status: ShipmentStatus;
    pickup_close: string;
    pickup_date: string;
    /**
     * Requested Date
     */
    pickup_date_2: string;
    pickup_date_old: string;
    pickup_location_city: string;
    pickup_location_state: string;
    pickup_name: string;
    pickup_ready: string;
    pod_files: string;
    pu_id: number;
    ready_date: string;
    service: string;
    shipper_id: string;
    shipper_pickup_no: string;
    stage: number;
    tms_group_id: number;
}

interface IAdjustedOrderLineDto {
    id: number;
    pallet: number;
    po: string;
    stackable: number;
    weight: number;
}

interface IMlineDto {
    fk_tms_order_id: number;
    isStackable: string;
    manifest_id: number;
    manifest_name: string;
    manifest_note: string;
    pallet: number;
    pallet_details_id: number | null;
}

type IShipment = Omit<IShipmentDto, 'ready_date'> & {
    editable: boolean;
    palletsStats: {
        nonstackable: number;
        stackable: number;
    };
    ready_date: string | undefined;
    total_weight: string;
    isDelete?: boolean;
};

function countPallets(dto: IShipment | IShipmentDto) {
    let stackable = 0;
    let nonstackable = 0;
    dto.order_adjusted.forEach(i => {
        // nonstackable
        if (i.stackable === 0) {
            nonstackable += i.pallet;
        }
        // stackable
        if (i.stackable !== 0) {
            stackable += i.pallet;
        }
    });

    return {
        nonstackable,
        stackable,
    };
}

// function countPallets(lines: IMlineDto[]) {
//     let stackable = 0;
//     let nonstackable = 0;

//     lines.forEach(i => {
//         // nonstackable
//         if (i.isStackable === '0') {
//             nonstackable += i.pallet;
//         }

//         // stackable
//         if (i.isStackable !== '0') {
//             stackable += i.pallet;
//         }
//     });

//     return {
//         nonstackable,
//         stackable,
//     };
// }

function calcTotalWeight(dto: IShipment | IShipmentDto) {
    const total = dto.order_adjusted.reduce((acc, cur) => acc + (Number(cur.weight) || 0), 0);
    return total.toFixed(2);
}

/**
 * 判断数据行时否可以编辑
 * @param dto
 * @returns
 */
function isEditable(dto: IShipment | IShipmentDto) {
    return dto.order_stage < 4 && dto.order_final != 1;
}

/**
 * 对API返回的数据列表进行预处理，比如统计运送货物的total weigh
 * @param lis
 * @returns
 */
function preprocess(list: IShipmentDto[]): Array<IShipment> {
    return list.map(i => {
        if (i.pickup_ready && i.pickup_ready !== '0') {
            i.pickup_ready = i.pickup_ready.substring(0, 5);
        } else {
            i.pickup_ready = '';
        }

        if (i.pickup_close && i.pickup_close !== '0') {
            i.pickup_close = i.pickup_close.substring(0, 5);
        } else {
            i.pickup_close = '';
        }

        return {
            ...i,
            editable: isEditable(i),
            palletsStats: countPallets(i),
            ready_date: i.ready_date && i.ready_date.length > 0 ? i.ready_date : undefined,
            total_weight: calcTotalWeight(i),
        };
    });
}

async function deleteOrderLine(dto: IAdjustedOrderLineDto) {
    const params = {
        id: dto.id,
    };
    const res = await tmsServiceBackend.post('/order/delete_adjust', qs.stringify(params));
    console.log('deleteOrderLine', res);
    return res;
}

/**
 * 判断给定的日期是否被禁用。
 *
 * 日期选择限制：
 * - 选择的日期必须晚于今天
 * - 选择的日期不能是周末或节假日
 * - 选择的日期不能晚于从明天开始计算的第 30 个可预订工作日
 *
 * @param row - 包含 requested date 的数据行对象。
 * @param time - 要检查的日期。
 * @returns 如果日期被禁用，则返回 true；否则返回 false。
 */
function isDisabledReadyDate(row: IShipment, time: any) {
    const today = Moment().startOf('day');
    const selectedDate = Moment(time).startOf('day');

    // 检查是否为美国联邦假日
    const isHoliday = (date: Moment.Moment) => {
        return [
            // New Year's Day
            date.month() === 0 && date.date() === 1,
            // Independence Day
            date.month() === 6 && date.date() === 4,
            // Christmas Eve
            date.month() === 11 && date.date() === 24
        ].some(isHoliday => isHoliday);
    };

    const isBookableWorkday = (date: Moment.Moment) => {
        const dayOfWeek = date.day();
        return dayOfWeek !== 0 && dayOfWeek !== 6 && !isHoliday(date);
    };

    // 检查是否为周末 (0 = 周日, 6 = 周六)
    const dayOfWeek = selectedDate.day();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
        return true;
    }

    // 检查是否为节假日
    if (isHoliday(selectedDate)) {
        return true;
    }

    // Only disable dates before today
    if (selectedDate.isBefore(today) || selectedDate.isSame(today)) {
        return true;
    }

    const latestReadyDate = today.clone();
    let bookableWorkdays = 0;
    while (bookableWorkdays < 30) {
        latestReadyDate.add(1, 'day');
        if (isBookableWorkday(latestReadyDate)) {
            bookableWorkdays += 1;
        }
    }

    return selectedDate.isAfter(latestReadyDate);
}

async function updateReadyDate(item: IShipment, reason: string, message: string) {
    // Ready date is already a string, no need to convert
    const params = {
        input_appointment_add_pid: item.pu_id,
        ready_date: item.ready_date,
        reason: reason ?? '',
        description: message ?? '',
    };
    const res = await tmsServiceBackend.post('/order/update_putime', qs.stringify(params));
    console.log('updateReadyDate', res);
    return res;
}

async function updateOrder(item: IShipment, reason: string, message: string) {
    // Ready date is already a string, no need to convert
    const params = {
        input_appointment_add_pid: item.pu_id,
        input_appointment_add_apt_num: item.shipper_pickup_no,
        input_appointment_add_apt_time_from: item.pickup_ready ? (item.pickup_ready + ':00') : '',
        input_appointment_add_apt_time_to: item.pickup_close ? (item.pickup_close + ':00') : '',
        ready_date: item.ready_date,
        reason: reason ?? '',
        description: message ?? '',
        input_note: item.order_note,
        input_order_ajusted: JSON.stringify(
            item.order_adjusted
                .filter(l => !isEmptyAdjustedOrderLine(l))
                .map(j => {
                    const item = {
                        pallet: j.pallet,
                        po: j.po,
                        stackable: j.stackable,
                        weight: j?.weight ? j.weight : 0,
                    } as Record<string, any>;
                    if (isNumber(j.id) && j.id > 0) {
                        item.id = j.id;
                    }
                    return item;
                })
        ),
        input_order_ref: item.order_ref,
        input_reason: item.order_reason,
    };

    // 过滤为空的参数
    const filteredParams = Object.entries(params)
        .filter(([_, value]) => value !== undefined && value !== '')
        .reduce((acc, [key, value]) => {
            acc[key] = value;
            return acc;
        }, {} as Record<string, any>);

    const res = await tmsServiceBackend.post('/order/update_putime', qs.stringify(filteredParams));
    console.log('updateOrder', res);
    return res;
}

async function updateReference(item: IShipment) {
    const params = {
        input_appointment_add_pid: item.pu_id,
        input_order_ref: item.order_ref,
    };
    const res = await tmsServiceBackend.post('/order/update_putime', qs.stringify(params));
    console.log('updateReadyDate', res);
    return res;
}

async function updateNotes(item: IShipment) {
    const params = {
        input_appointment_add_pid: item.pu_id,
        input_note: item.order_note,
    };
    const res = await tmsServiceBackend.post('/order/update_putime', qs.stringify(params));
    console.log('updateNotes', res);
    return res;
}

async function updatePickupReadyNPickupClose(item: IShipment) {
    const params = {
        input_appointment_add_pid: item.pu_id,
        input_appointment_add_apt_num: item.shipper_pickup_no,
        input_appointment_add_apt_time_from: item.pickup_ready + ':00',
        input_appointment_add_apt_time_to: item.pickup_close + ':00',
    };
    const res = await tmsServiceBackend.post(`/order/update_putime`, qs.stringify(params));
    console.log('updatePickupReady', res);
    return res;
}

async function updateShipment(item: IShipment, deletedIds: string[]) {
    const params = {
        input_appointment_add_pid: item.pu_id,
        input_note: item.order_note,
        input_order_ajusted: JSON.stringify(
            item.order_adjusted
                .filter(l => !isEmptyAdjustedOrderLine(l))
                .map(j => {
                    const item = {
                        pallet: j.pallet,
                        po: j.po,
                        stackable: j.stackable,
                        weight: j?.weight ? j.weight : 0,
                    } as Record<string, any>;
                    if (isNumber(j.id) && j.id > 0) {
                        item.id = j.id;
                    }
                    return item;
                })
        ),
        input_order_ref: item.order_ref,
        input_reason: item.order_reason,
        deleted_list: deletedIds.length > 0 ? deletedIds.join(',') : '',
    };
    const res = await tmsServiceBackend.post('/order/update_putime', qs.stringify(params));
    return res;
}

function getShipmentPO(i: IShipment) {
    return (i.order_adjusted || [])
        .filter(item => item.po)
        .map(item => item.po)
        .join('/');
}

function isEmptyAdjustedOrderLine(line: IAdjustedOrderLineDto) {
    return (
        line.po.length === 0 &&
        (isNil(line.pallet) || line.pallet === 0) &&
        (isNil(line.weight) || line.weight === 0) &&
        isNil(line.stackable)
    );
}

/**
 * 验证调整订单行是否包含有效托盘数
 * @param lines - 需要验证的调整订单行数组
 * @returns 如果所有非空行都有有效托盘数则返回true，否则返回false
 */
function validatePalletCounts(lines: IAdjustedOrderLineDto[]): boolean {
    return lines
        .filter(line => !isEmptyAdjustedOrderLine(line))
        .every(line => line.pallet > 0);
}

/**
 * 验证Ready Time和Close Time是否都已填写
 * @param item - 需要验证的数据行
 * @returns 如果两个时间都已填写则返回true，否则返回false
 */
function validatePickupTimes(item: IShipment): { isValid: boolean; missingFields: string[] } {
    const missingFields: string[] = [];

    if (!item.pickup_ready) {
        missingFields.push('Ready Time');
    }

    if (!item.pickup_close) {
        missingFields.push('Close Time');
    }

    return {
        isValid: missingFields.length === 0,
        missingFields
    };
}

export {
    calcTotalWeight,
    countPallets,
    deleteOrderLine,
    getShipmentPO,
    IAdjustedOrderLineDto,
    IMlineDto,
    isDisabledReadyDate,
    isEmptyAdjustedOrderLine,
    IShipment,
    IShipmentDto,
    preprocess,
    updateNotes,
    updatePickupReadyNPickupClose,
    updateReadyDate,
    updateReference,
    updateShipment,
    validatePalletCounts,
    validatePickupTimes,
    updateOrder,
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--1!./src/layouts/shipments-4-shippers/components/shipments/types.ts